/* eslint-disable jsx-a11y/anchor-is-valid */
import { useActivation } from '@context/MobileOne';
import { useA11y } from '@context/Utils';
import {
  ApiError,
  ButtonPrimary,
  DropdownList,
  FormInputField,
  NotificationSuggestion,
  ScrollToTop
} from '@core/index';
import Icons from '@core/Utils/Icons/Icons';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { appButtonType, appIconsPath, notificationType, validCharRegex } from '@utils/globalConstant';
import { Form, Formik } from 'formik';

export function EditAddressData() {
  // Contexts
  const {
    isLoading,
    personalDataForm,
    setStep8SubStep,
    personalDataFormSubmit,
    countries,
    formError,
    setFormError,
    validateAddressRes,
    personalDataValidations
  } = useActivation();
  const { isGenericApiError } = useA11y();

  // const
  const headerContent = tA11y('ek_reg_change_pers_data_hdl1');
  const buttonContent = tA11y('ek_reg_change_btn1');
  const countryLabelContent = tA11y('ek_global_country_lbl');
  const countryPlaceholderContent = tA11y('ek_global_country_plc');
  const successBoxTitle = tA11y('ek_reg_adress_vldtr_hdl1');
  const successBoxText = tA11y('ek_reg_adress_vldtr_txt1');
  const editAddressDataSchema = personalDataValidations.pick([
    'street',
    'houseNumber',
    'zip',
    'city',
    'countryCode'
  ]);

  // Function
  const onBackClick = () => {
    setStep8SubStep(0);
  };

  const handleAPIErrorLink = () => {
    const firstInput = document.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    }
  }

  return (
    <>
      <ScrollToTop>
        <div className="min-dvh-100">
          <AppPageContentWrapper>
            <div className="mnp-activation w-100">
              <button
                type="button"
                className="border-0 bg-transparent m-0 p-0 mb-32"
                aria-label={tA11y('ek_global_icn-back_aria').ariaLabel}
                onClick={onBackClick}>
                <Icons name='ARROW_BACK' height={24} colorType='dark' />
              </button>

              {headerContent?.content !== '' && headerContent?.content !== undefined && (
                <>
                  {headerContent?.isHtml ? (
                    <h2
                      className="ek-h2 text-primary mb-40"
                      dangerouslySetInnerHTML={{ __html: headerContent?.content }}
                    />
                  ) : (
                    <h2 className="ek-h2 text-primary mb-40">{headerContent?.content}</h2>
                  )}
                </>
              )}

              <Formik
                initialValues={personalDataForm}
                validationSchema={editAddressDataSchema}
                onSubmit={personalDataFormSubmit}>
                {({
                  values: { street, houseNumber, zip, city, countryCode },
                  handleBlur,
                  handleChange,
                  setFieldValue,
                  handleSubmit,
                  touched,
                  errors
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <FormInputField
                      label="ek_global_street_lbl"
                      placeholder="ek_global_street_plc"
                      type="text"
                      id="street"
                      name="street"
                      value={street}
                      onChange={(e) => {
                        handleChange(e);
                        setFormError(false);
                      }}
                      onClear={setFieldValue}
                      onBlur={handleBlur}
                      valid={touched.street && !errors.street}
                      invalid={touched.street && errors.street || !!(formError && !isGenericApiError)}
                      invalidMessage={touched.street && errors.street ? errors.street : null}
                      aria-describedby="street-error"
                    />
                    <FormInputField
                      customClass="mt-32"
                      label="ek_global_house_number_lbl"
                      placeholder="ek_global_house_number_plc"
                      type="text"
                      id="houseNumber"
                      name="houseNumber"
                      value={houseNumber}
                      onChange={(e) => {
                        handleChange(e);
                        setFormError(false);
                      }}
                      onClear={setFieldValue}
                      onBlur={handleBlur}
                      valid={touched.houseNumber && !errors.houseNumber}
                      invalid={touched.houseNumber && errors.houseNumber || !!(formError && !isGenericApiError)}
                      invalidMessage={
                        touched.houseNumber && errors.houseNumber ? errors.houseNumber : null
                      }
                      aria-describedby="houseNumber-error"
                    />
                    <FormInputField
                      customClass="mt-32"
                      label="ek_global_zip_lbl"
                      placeholder="ek_global_zip_plc"
                      type="text"
                      id="zip"
                      name="zip"
                      value={zip}
                      onChange={(e) => {
                        handleChange(e);
                        setFormError(false);
                      }}
                      onClear={setFieldValue}
                      onBlur={handleBlur}
                      valid={touched.zip && !errors.zip}
                      invalid={touched.zip && errors.zip || !!(formError && !isGenericApiError)}
                      invalidMessage={touched.zip && errors.zip ? errors.zip : null}
                      aria-describedby="zip-error"
                      checkCharRegex={validCharRegex.ZIP_CODE}
                    />
                    <FormInputField
                      customClass="my-32"
                      label="ek_global_place_lbl"
                      placeholder="ek_global_place_plc"
                      type="text"
                      id="city"
                      name="city"
                      value={city}
                      onChange={(e) => {
                        handleChange(e);
                        setFormError(false);
                      }}
                      onClear={setFieldValue}
                      onBlur={handleBlur}
                      valid={touched.city && !errors.city}
                      invalid={touched.city && errors.city || !!(formError && !isGenericApiError)}
                      invalidMessage={touched.city && errors.city ? errors.city : null}
                      aria-describedby="city-error"
                    />
                    <DropdownList
                      id="countryCode"
                      name="countryCode"
                      label={countryLabelContent?.content}
                      value={countryCode}
                      placeholder={countryPlaceholderContent?.content}
                      onChange={(value) => setFieldValue('countryCode', value)}
                      onBlur={handleBlur}
                      options={(countries || []).map((code) => ({
                        id: code.id,
                        label: code.value
                      }))}
                      valid={touched.countryCode && !errors.countryCode}
                      invalid={touched.countryCode && errors.countryCode}
                      invalidMessage={
                        touched.countryCode && errors.countryCode ? errors.countryCode : null
                      }
                      isRequired
                      aria-describedby="countryCode-error"
                    />
                    {formError && (
                      <div className="form-text mt-40 ps-4" aria-live="polite">
                        <ApiError message={formError} isGenericApiError={isGenericApiError} handleAPIErrorLink={handleAPIErrorLink}/>
                      </div>
                    )}

                    {validateAddressRes && (
                      <div className="pt-40">
                        <NotificationSuggestion
                          type={notificationType.VARIANT2}
                          title={successBoxTitle?.content}
                          text={successBoxText?.content}
                          message={[
                            `${validateAddressRes?.street} ${validateAddressRes?.houseNumber}`,
                            `${validateAddressRes?.zip} ${validateAddressRes?.city}`
                          ]}
                        />
                      </div>
                    )}
                    <div className="row">
                      <div className="col-xxl-5 col-lg-5 col-10 px-0 mx-auto">
                        <ButtonPrimary
                          buttonClass="mt-40"
                          buttonType={appButtonType.SECONDARY}
                          buttonContent={buttonContent}
                          aria-label={tA11y('ek_global_icn-back_aria').ariaLabel}
                          iconLeft={appIconsPath.ARROW_BACK}
                          type="submit"
                          isLoading={isLoading}
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </AppPageContentWrapper>
        </div>
      </ScrollToTop>
    </>
  );
}

export default EditAddressData;
